import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Check, Square, Link as LinkIcon, Copy, X, Plus, Clock,
  Activity as ActivityIcon, Eye, Shield, Calendar
} from "lucide-react";
import {
  drListFiles, drInvestorAssignments, drCreateAssignment, drDeleteAssignment,
  drListMagicLinks, drCreateMagicLink, drDeactivateMagicLink,
} from "../../lib/api";
import { fmtDate } from "../../lib/format";

const fmtSize = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1024/1024).toFixed(1)} MB`;
};

const formatToken = (t) => t ? `${t.slice(0,8)}…${t.slice(-4)}` : "";

const defaultAblauf = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0,10);
};

export default function DatenraumFreigabeTab({ investorId, firma }) {
  const [allFiles, setAllFiles] = useState([]);
  const [assignmentsByFile, setAssignmentsByFile] = useState({});  // file_id → assignment_id (oder null)
  const [magicLinks, setMagicLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Magic-Link-Form
  const [showCreate, setShowCreate] = useState(false);
  const [ablauf, setAblauf] = useState(defaultAblauf());
  const [maxZ, setMaxZ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [filesR, assR, mlR] = await Promise.all([
        drListFiles({ nur_aktiv: true }),
        drInvestorAssignments(investorId),
        drListMagicLinks(investorId),
      ]);
      setAllFiles(filesR.items || []);
      // Map: file_id → assignment_id (für diesen Investor; globale Assignments werden ignoriert in dieser UI)
      const map = {};
      (assR.items || []).forEach(a => {
        if (a.investor_id === investorId) {
          map[a.file_id] = a.id;
        }
      });
      setAssignmentsByFile(map);
      setMagicLinks(mlR.items || []);
    } catch (e) {
      console.error(e);
      toast.error("Konnte Datenraum-Freigaben nicht laden");
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  useEffect(() => { load(); }, [load]);

  const toggleAssignment = async (file) => {
    const existing = assignmentsByFile[file.id];
    try {
      if (existing) {
        await drDeleteAssignment(existing);
        toast.success(`„${file.filename_original}" Freigabe entfernt`);
      } else {
        await drCreateAssignment(file.id, investorId);
        toast.success(`„${file.filename_original}" für ${firma} freigegeben`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const handleCreateLink = async () => {
    if (!ablauf) { toast.error("Ablaufdatum erforderlich"); return; }
    setCreating(true);
    try {
      const ml = await drCreateMagicLink(investorId, ablauf, maxZ ? parseInt(maxZ, 10) : null);
      toast.success("Magic-Link erstellt — direkt aus der Tabelle kopieren");
      setShowCreate(false);
      setAblauf(defaultAblauf());
      setMaxZ("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erstellen fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (token) => {
    if (!window.confirm("Magic-Link wirklich deaktivieren? Der Empfänger kann den Datenraum dann nicht mehr öffnen.")) return;
    try {
      await drDeactivateMagicLink(token);
      toast.success("Link deaktiviert");
      await load();
    } catch (e) {
      toast.error("Deaktivieren fehlgeschlagen");
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/datenraum/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link in Zwischenablage kopiert");
    } catch {
      // Fallback
      prompt("Link manuell kopieren:", url);
    }
  };

  const assignedCount = Object.keys(assignmentsByFile).length;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6 py-4" data-testid="drawer-datenraum-freigabe-tab">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="pb-card text-center">
          <div className="pb-eyebrow">Freigegeben</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{assignedCount}</div>
          <div className="text-[10px] text-navy/55 font-sans">von {allFiles.length} Dateien</div>
        </div>
        <div className="pb-card text-center">
          <div className="pb-eyebrow">Magic-Links</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">
            {magicLinks.filter(m => m.status === "aktiv").length}
          </div>
          <div className="text-[10px] text-navy/55 font-sans">aktiv / {magicLinks.length} gesamt</div>
        </div>
        <div className="pb-card text-center">
          <div className="pb-eyebrow">Zugriffe</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">
            {magicLinks.reduce((s, m) => s + (m.anzahl_zugriffe || 0), 0)}
          </div>
          <div className="text-[10px] text-navy/55 font-sans">Downloads total</div>
        </div>
      </div>

      {/* Freigabe-Matrix */}
      <section data-testid="drawer-dr-freigabe-matrix">
        <div className="pb-eyebrow mb-3 flex items-center gap-2">
          <Shield size={11}/> Dokument-Freigaben für {firma}
        </div>
        {loading && <div className="text-xs text-navy/55 font-sans py-4">Lade…</div>}
        {!loading && allFiles.length === 0 && (
          <div className="text-xs text-navy/55 font-sans py-6 text-center border border-dashed border-navy/20">
            Keine Dateien im Datenraum. Zuerst unter „Datenraum" Dateien hochladen.
          </div>
        )}
        {!loading && allFiles.length > 0 && (
          <div className="border border-navy/15 max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-ledger sticky top-0">
                <tr className="text-left border-b border-navy/15">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans">Datei</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans">Kategorie</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans w-16 text-center">Freigabe</th>
                </tr>
              </thead>
              <tbody>
                {allFiles.map(f => {
                  const isAssigned = !!assignmentsByFile[f.id];
                  return (
                    <tr key={f.id} className="border-b border-navy/10 hover:bg-ledger/50">
                      <td className="px-3 py-2">
                        <div className="text-navy text-xs font-sans truncate max-w-[280px]">{f.filename_original}</div>
                        <div className="text-[10px] text-navy/45 font-sans">{fmtSize(f.size_bytes)}{f.version ? ` · ${f.version}` : ""}</div>
                      </td>
                      <td className="px-3 py-2 text-xs font-sans text-navy/75">{f.kategorie}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => toggleAssignment(f)}
                          data-testid={`dr-toggle-${f.id}`}
                          className={`inline-flex items-center justify-center w-7 h-7 ${
                            isAssigned
                              ? "bg-gold text-white border border-gold"
                              : "bg-white border border-navy/25 text-navy/45 hover:border-gold hover:text-gold"
                          }`}
                          title={isAssigned ? "Freigabe entfernen" : "Freigeben"}
                        >
                          {isAssigned ? <Check size={14}/> : <Square size={14}/>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Magic-Links */}
      <section data-testid="drawer-dr-magic-links">
        <div className="flex items-center justify-between mb-3">
          <div className="pb-eyebrow flex items-center gap-2">
            <LinkIcon size={11}/> Magic-Links für {firma}
          </div>
          {!showCreate && (
            <button
              data-testid="dr-create-link-btn"
              onClick={() => setShowCreate(true)}
              className="pb-btn-primary gap-2 text-xs"
            >
              <Plus size={12}/> Neuen Link erstellen
            </button>
          )}
        </div>

        {showCreate && (
          <div className="pb-card mb-3" data-testid="dr-create-link-form">
            <div className="grid grid-cols-12 gap-3 items-end">
              <label className="col-span-5">
                <span className="text-[10px] uppercase tracking-widish text-navy/55 font-sans flex items-center gap-1">
                  <Calendar size={10}/> Gültig bis
                </span>
                <input
                  type="date"
                  data-testid="dr-link-ablauf"
                  value={ablauf}
                  min={today}
                  onChange={(e) => setAblauf(e.target.value)}
                  className="pb-input--full mt-1"
                />
              </label>
              <label className="col-span-3">
                <span className="text-[10px] uppercase tracking-widish text-navy/55 font-sans">Max. Zugriffe</span>
                <input
                  type="number"
                  min="1"
                  data-testid="dr-link-max"
                  value={maxZ}
                  onChange={(e) => setMaxZ(e.target.value)}
                  placeholder="optional"
                  className="pb-input--full mt-1"
                />
              </label>
              <button
                data-testid="dr-link-submit"
                onClick={handleCreateLink}
                disabled={creating}
                className="pb-btn-primary col-span-2 gap-1"
              >
                <Plus size={13}/> {creating ? "..." : "Erstellen"}
              </button>
              <button
                onClick={() => { setShowCreate(false); setAblauf(defaultAblauf()); setMaxZ(""); }}
                className="pb-btn-outline col-span-2 gap-1"
              >
                <X size={13}/> Abbruch
              </button>
            </div>
          </div>
        )}

        {magicLinks.length === 0 && !loading && (
          <div className="text-xs text-navy/55 font-sans py-6 text-center border border-dashed border-navy/20">
            Noch keine Magic-Links für diesen Investor.
          </div>
        )}

        {magicLinks.length > 0 && (
          <div className="border border-navy/15">
            <table className="w-full text-sm">
              <thead className="bg-ledger">
                <tr className="text-left border-b border-navy/15">
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans">Token</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans">Status</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans">Ablauf</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans text-right">Zugriffe</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans">Erstellt</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-widish text-navy/55 font-sans"></th>
                </tr>
              </thead>
              <tbody>
                {magicLinks.map(m => {
                  const statusClass = m.status === "aktiv" ? "text-heiss-dark bg-heiss/15"
                    : m.status === "abgelaufen" ? "text-tot bg-tot/10"
                    : "text-navy/45 bg-navy/10";
                  return (
                    <tr key={m.token} className="border-b border-navy/10" data-testid={`dr-magic-link-${m.token.slice(0,8)}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-navy/75">{formatToken(m.token)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-sans ${statusClass}`}>
                          {m.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs font-sans text-navy/75">{m.ablauf_datum}</td>
                      <td className="px-3 py-2 text-xs font-sans text-navy/75 text-right tabular-nums">
                        {m.anzahl_zugriffe}{m.max_zugriffe ? ` / ${m.max_zugriffe}` : ""}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-sans text-navy/55">
                        {fmtDate(m.erstellt_am)}<br/>
                        <span className="text-navy/45">{m.erstellt_von}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {m.status === "aktiv" && (
                          <>
                            <button
                              onClick={() => copyLink(m.token)}
                              data-testid={`dr-copy-${m.token.slice(0,8)}`}
                              className="text-navy/65 hover:text-gold inline-flex items-center gap-1 text-xs"
                              title="Link kopieren"
                            >
                              <Copy size={12}/>
                            </button>
                            <button
                              onClick={() => handleDeactivate(m.token)}
                              data-testid={`dr-deactivate-${m.token.slice(0,8)}`}
                              className="ml-3 text-navy/65 hover:text-tot inline-flex items-center text-xs"
                              title="Deaktivieren"
                            >
                              <X size={12}/>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
